/**
 * Advanced Analytics Types
 * RFM Analysis, Customer Segmentation, etc.
 */

export enum CustomerSegment {
  NEW = 'NEW',
  OCCASIONAL = 'OCCASIONAL',
  REGULAR = 'REGULAR',
  FREQUENT = 'FREQUENT',
  VIP = 'VIP',
  AT_RISK = 'AT_RISK',
  CHURNED = 'CHURNED',
  DORMANT = 'DORMANT',
}

export interface RFMScore {
  customerId: string;
  customerEmail?: string;
  customerPhone?: string;
  customerName?: string;

  // Recency
  daysSinceLastOrder: number;
  lastOrderDate: string;
  recencyScore: number; // 1-5

  // Frequency
  totalOrders: number;
  frequencyScore: number; // 1-5

  // Monetary
  totalSpend: number;
  averageOrderValue: number;
  monetaryScore: number; // 1-5

  // Combined
  rfmScore: number; // 3-15
  segment: CustomerSegment;

  // Lifetime
  firstOrderDate: string;
  customerLifetimeDays: number;
  lifetimeValue: number;
}

export interface CustomerSegmentation {
  // Distribution
  customersBySegment: Record<CustomerSegment, number>;
  revenueBySegment: Record<CustomerSegment, number>;

  // Top customers
  topCustomers: RFMScore[];
  vipCustomers: RFMScore[];
  atRiskCustomers: RFMScore[];

  // Summary
  totalCustomers: number;
  newCustomers: number;
  returningCustomers: number;
  activeCustomers: number;
  churnedCustomers: number;

  // Metrics
  customerRetentionRate: number; // percentage
  churnRate: number; // percentage
  repeatCustomerRate: number; // percentage
  averageCustomerLifetimeValue: number;
  averageOrdersPerCustomer: number;
  averageSpendPerCustomer: number;
}

// Segment colors for UI
export const SEGMENT_COLORS: Record<CustomerSegment, string> = {
  [CustomerSegment.VIP]: '#8B5CF6', // purple
  [CustomerSegment.FREQUENT]: '#10B981', // green
  [CustomerSegment.REGULAR]: '#3B82F6', // blue
  [CustomerSegment.OCCASIONAL]: '#6B7280', // gray
  [CustomerSegment.NEW]: '#F59E0B', // amber
  [CustomerSegment.AT_RISK]: '#EF4444', // red
  [CustomerSegment.CHURNED]: '#DC2626', // dark red
  [CustomerSegment.DORMANT]: '#991B1B', // darker red
};

// Segment labels in Italian
export const SEGMENT_LABELS: Record<CustomerSegment, string> = {
  [CustomerSegment.VIP]: 'VIP',
  [CustomerSegment.FREQUENT]: 'Frequenti',
  [CustomerSegment.REGULAR]: 'Regolari',
  [CustomerSegment.OCCASIONAL]: 'Occasionali',
  [CustomerSegment.NEW]: 'Nuovi',
  [CustomerSegment.AT_RISK]: 'A Rischio',
  [CustomerSegment.CHURNED]: 'Persi',
  [CustomerSegment.DORMANT]: 'Inattivi',
};

// Cohort Analysis Types
export interface CohortDataPoint {
  acquisitionMonth: string; // Format: "2025-01"
  customersAcquired: number;
  retentionByMonth: Record<number, number>; // monthOffset -> active customers
  retentionRateByMonth: Record<number, number>; // monthOffset -> retention %
  cohortRevenue: number;
  avgRevenuePerCustomer: number;
}

export interface CohortAnalysis {
  cohorts: CohortDataPoint[];
  averageRetentionMonth1: number; // %
  averageRetentionMonth3: number; // %
  averageRetentionMonth6: number; // %
}

// Menu Engineering Types
export enum MenuCategory {
  STAR = 'STAR',
  PLOWHORSE = 'PLOWHORSE',
  PUZZLE = 'PUZZLE',
  DOG = 'DOG',
}

export interface MenuItemAnalysis {
  productId: string;
  productName: string;
  categoryName: string;
  imageUrl?: string;

  // Sales metrics
  quantitySold: number;
  revenue: number;
  orderCount: number;
  popularityIndex: number; // %

  // Profitability
  unitCost: number;
  unitPrice: number;
  contributionMargin: number;
  contributionMarginRatio: number; // %
  totalProfit: number;

  // Classification
  menuCategory: MenuCategory;
  popularityRank: number;
  profitabilityRank: number;

  // Recommendations
  recommendation: string;
  suggestedPrice?: number;
}

export interface MenuEngineering {
  stars: MenuItemAnalysis[];
  plowhorses: MenuItemAnalysis[];
  puzzles: MenuItemAnalysis[];
  dogs: MenuItemAnalysis[];
  totalMenuItems: number;
  averageContributionMargin: number;
  averagePopularityIndex: number;
}

// Menu category colors and labels
export const MENU_CATEGORY_COLORS: Record<MenuCategory, string> = {
  [MenuCategory.STAR]: '#10B981', // green
  [MenuCategory.PLOWHORSE]: '#F59E0B', // amber
  [MenuCategory.PUZZLE]: '#8B5CF6', // purple
  [MenuCategory.DOG]: '#EF4444', // red
};

export const MENU_CATEGORY_LABELS: Record<MenuCategory, string> = {
  [MenuCategory.STAR]: '⭐ Star',
  [MenuCategory.PLOWHORSE]: '🐴 Plowhorse',
  [MenuCategory.PUZZLE]: '🧩 Puzzle',
  [MenuCategory.DOG]: '🐕 Dog',
};

export const MENU_CATEGORY_DESCRIPTIONS: Record<MenuCategory, string> = {
  [MenuCategory.STAR]: 'Alta popolarità, alto profitto - Mantieni e promuovi!',
  [MenuCategory.PLOWHORSE]: 'Alta popolarità, basso profitto - Considera aumento prezzo',
  [MenuCategory.PUZZLE]: 'Bassa popolarità, alto profitto - Migliora marketing',
  [MenuCategory.DOG]: 'Bassa popolarità, basso profitto - Valuta rimozione',
};
